import { 
	API_ENDPOINTS, 
	Button, 
	ButtonTypes, 
	Card, 
	CardBody, 
	customToast,   
	Dropdown, 
	FileHelpers,
	formatDate, 
	getAdminUrl, 
	Icons, 
	PageLayout, 
	Permissions, 
	replaceKeyWithValue, 
	Roles, 
	route, 
	StringHelpers, 
	SubTitle, 
	useAuthorisationContext, 
	useDisabledContext, 
	useLangContext, 
	useStore, 
	useUserContext, 
	Variants, 
	WidthConstrainedContainer 
} from "carrier-fe";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from 'axios';
import { ToshibaInstallation as ToshibaInstallationType } from "../../../../types/installation";
import { DropdownItemProps } from "carrier-fe/lib/components/Controls/Dropdown/DropdownItem/DropdownItem";
import { MAX_TEXT_LENGTH, PRODUCT_LINES, STATUS_TYPES, STEPS, TOTAL_STEPS } from "../../../../util/Constants";
import ArchiveCancelModal from "../../../../components/ArchiveCancelModal";
import ConfirmationModal, { ConfirmationModalTypes } from "../../../../components/EmailConfirmationModal";
import InstallationEventLog from "../../eventLog";

function ToshibaInstallation() {
    const { installationId } = useParams();
    const { crud, fields } = useLangContext();
    const { disabled } = useDisabledContext();
	const { hasRoleAccess, hasPermissionAccess } = useAuthorisationContext();
	const { user } = useUserContext();
    const location = useLocation();
    const navigate = useNavigate();
	const adminUrl = getAdminUrl();

    const [activeModal, setActiveModal] = useState<{
		isOpen: boolean;
		type: 'archive' | 'cancel' | null;
	}>({
		isOpen: false,
		type: null,
	});

	const [emailModalState, setEmailModalState] = useState({
		isOpen: false,
		type: '' as ConfirmationModalTypes | '',
	});

    const { store, setValues } = useStore<ToshibaInstallationType>(
		{} as ToshibaInstallationType
	);

    useEffect(() => {
        fetchInstallationData();
    }, []);

    const handleBack = () => {
		const searchParams = new URLSearchParams(location.search);
		const fromPath = searchParams.get('from');
		const savedFilter = searchParams.get('filter[search]');

		switch (fromPath) {
			case 'warranties': {
				navigate(
					savedFilter
						? `/warranties?filter[search]=${encodeURIComponent(savedFilter)}`
						: '/warranties'
				);
				break;
			}
			default: {
				navigate(
					savedFilter
						? `/installation?filter[search]=${encodeURIComponent(savedFilter)}`
						: '/installation'
				);
			}
		}
	};

    const fetchInstallationData = async () => {
		try {
		const { data } = await axios.get<{ data: ToshibaInstallationType }>(
			route(API_ENDPOINTS.REGISTRATION.INSTALLATION.VIEW, {
				installationId: String(installationId),
			}), 
		);
			setValues(data.data);
		} catch (err: any) {
			customToast({
				title: err?.response?.data?.message || '',
				variant: Variants.Danger,
			});
		} 
	};

	const downloadWarrantyCertificate = async () => {
		try {
			await FileHelpers.downloadFile(
				route(
					API_ENDPOINTS.REGISTRATION.INSTALLATION
						.WARRANTY_CERTIFICATE,
					{
						installationId: installationId || '',
					}
				),
				'warrantyCertificate.pdf'
			);
			customToast({
				title: 'Warranty Certificate Downloaded Successfully',
				variant: Variants.Success,
			});
		} catch (error: any) {
			const errorMsg = error.response?.data ? await error.response.data.text() : error.message;
			customToast({
				title: JSON.parse(errorMsg)?.message || '',
				variant: Variants.Danger,
			});
		}
	};

	const calculateNextStep = (): number => {
    if (!store?.last_submitted_step) return STEPS.STEP_1;

	if (hasRoleAccess(Roles.PLATFORM_MANAGER) && store?.status === STATUS_TYPES.COMPLETED) {
		const totalSteps = store?.skip_step_5 || !store?.add_a2a
			? TOTAL_STEPS - 1
			: TOTAL_STEPS;
		return totalSteps;
	};
    
	const isA2A = store?.skip_step_5 && store?.last_submitted_step === STEPS.STEP_6;
    return store.last_submitted_step + (isA2A ? 0 : 1);
};

    const getActions = (): DropdownItemProps[] => {
		if (!store || !store.permissions) return [];

		let actions: DropdownItemProps[] = [];

		if (store?.permissions?.update) {
			actions.push({
				label: StringHelpers.title(
					crud?.buttons?.edit?.default || 'Update'
				),
				onClick: () =>
					navigate(
						`/installation/${installationId}/edit?step=${calculateNextStep()}`
					),
				variant: Variants.Dark,
				icon: Icons.EDIT,
				disabled: disabled,
			});
		}

		if (store?.permissions?.email) {
			actions.push({
				label: StringHelpers.title(
					replaceKeyWithValue(
						crud?.modals?.resend_email?.title
							|| 'Re-send :name email?',
						'name',
						crud?.models?.installation || 'Installation'
					)
				),
				onClick: () =>
					setEmailModalState({
						isOpen: true,
						type: 'installationEmail',
					}),
				variant: Variants.Dark,
				icon: Icons.MAIL_SEND,
				disabled: disabled,
			});
		}

		if (
			store?.permissions.view &&
			store?.status === STATUS_TYPES.COMPLETED &&
			store?.active_warranty_exists
		) {
			actions.push({
				label: StringHelpers.title(
					'Warranty Certificate'
				),
				onClick: downloadWarrantyCertificate,
				variant: Variants.Dark,
				icon: Icons.DOWNLOAD,
				disabled: disabled,
			});
		}

		if (store?.permissions.cancel) {
			actions.push({
				label: StringHelpers.title(
					crud?.buttons.cancel.default || 'Cancel'
				),
				onClick: () =>
					setActiveModal({
						isOpen: true,
						type: 'cancel',
					}),
				variant: Variants.Dark,
				icon: Icons.CANCEL,
				disabled: disabled,
			});
		}

		if (store?.permissions.delete) {
			actions.push({
				label: StringHelpers.title(
					crud?.buttons.archive.default || 'Archive'
				),
				onClick: () =>
					setActiveModal({
						isOpen: true,
						type: 'archive',
					}),
				variant: Variants.Danger,
				icon: Icons.DELETE,
				disabled: disabled,
			});
		}

		return actions;
	};

    const title = useMemo(
		() =>
			`${replaceKeyWithValue(
				crud?.pages?.view.title 
                    || 'View :model',
				'model',
		        crud?.models?.installation
                    || 'Installation'
			)} ${crud?.sub_titles?.details
                    || 'Details'
            }`,
		[crud]
	);

    return (
        <PageLayout
            title={title}
        >
            <WidthConstrainedContainer>
                <div className="mb-4 d-flex justify-content-between align-items-center">
                    <Button
                        label={StringHelpers.title(
                            crud?.buttons?.back.default || 'Back'
                        )}
                        onClick={handleBack}
                        variant={Variants.Dark}
                        type={ButtonTypes.Outline}
                        disabled={disabled}
                    />
                    {getActions().length > 0 && (
                        <Dropdown
                            label={StringHelpers.title(
                                crud?.buttons?.actions.default ||
                                'Actions'
                            )}
                            items={getActions()}
                            type={ButtonTypes.Solid}
                            variant={Variants.Info}
                            disabled={disabled}
                        />
                    )}
                </div>

				{/* INSTALLATION DETAILS */}
				<Card>
					<CardBody>
						<SubTitle
							className='mb-3 w-100'
							style={{ color: '#464C5E' }}
							title={`${
								crud?.models?.installation
									|| 'Installation'
								} ${crud?.sub_titles?.details
									|| 'Details'
								}`
							}
						/>

						<div className='row'>
							<div className='col'>
								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											fields?.installer_company 
											|| 'Installation company'
										)}
									</strong>
									{store?.installer_company_id 
										&& store?.installer_company?.permissions?.view ? 
											<a href={`${adminUrl}/companies/${store?.installer_company_id}`}>
												{StringHelpers.title(
													store?.installer_company?.name 
													|| '-'
												)}
											</a>
										: 
											<div>
												{StringHelpers.title(
													store?.installer_company?.name 
													|| '-'
												)}
											</div>
									}
								</div>

								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											fields?.warranty_level
											|| 'Warranty level'
										)}
									</strong>
									<div>
										{store?.company_warranty_level?.name
											|| '-'
										}
									</div>
								</div>

								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											`${fields?.commissioning
												|| 'Commissioning'
											} ${fields?.event_at
												|| 'Date'
											}`
										)}
									</strong>
									<div>
										{formatDate(store?.commissioning_date) || '-'}
									</div>
								</div>
							</div>

							<div className='col'>
								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											`${fields?.commissioning
												|| 'Commissioning'
											} ${crud?.models?.engineer
												|| 'Engineer'
											}`
										)}
									</strong>
									<div>
										{store?.installer_individual?.name
											|| '-'
										}
									</div>
								</div>
							
								<div className='d-flex flex-column mb-3'>
									<strong>
										{crud?.sub_titles?.distributor
											|| 'Distributor'
										}
									</strong>
									<div>
										{store?.distributor_name
											|| '-'
										}
									</div>
								</div>
							</div>

							<div className='col'>
								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											fields?.status
											|| 'status'
										)}
									</strong>
									<div>
										{store?.status_display
											|| '-'
										}
									</div>
								</div>

								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											fields?.referer
											|| 'Referer'
										)}
									</strong>
									<div>
										{store?.referer
											|| '-'
										}
									</div>
								</div>

								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											fields?.created_at
											|| 'created'
										)}
									</strong>
									<div>
										{formatDate(store?.created_at)}
									</div>
								</div>
							</div>

							<div className='col'>
								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											fields?.updated_at
											|| 'updated'
										)}
									</strong>
									<div>
										{formatDate(store?.updated_at)}
									</div>
								</div>

								{store?.deleted_at && 
									<div className='d-flex flex-column mb-3'>
										<strong>
											{StringHelpers.title(
												fields?.archived_at
												|| 'archived'
											)}
										</strong>
										<div>
											{formatDate(store?.deleted_at)}
										</div>
									</div>
								}
							</div>
						</div>
					</CardBody>
				</Card>

				{/* CUSTOMER DETAILS */}
				<Card>
					<CardBody>
						<SubTitle
							className='mb-3 w-100'
							style={{ color: '#464C5E' }}
							title={`${
								crud?.models?.customer
									|| 'Customer'
								} ${crud?.sub_titles?.details
									|| 'Details'
								}`
							}
						/>

						<div className='row'>
							<div className='col'>
								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											fields?.name 
											|| 'name'
										)}
									</strong>
									{store?.customer_company?.id
										&& store?.customer_company?.primary_individual?.permissions?.view ?
											<a href={`/customers/${store?.customer_company?.id}`}>
												{StringHelpers.title(
													store?.customer_company?.primary_individual?.name 
													|| '-'
												)}
											</a>
										:
											<div>
												{store?.customer_company?.primary_individual?.name 
													|| '-'
												}
											</div>
									}
								</div>

								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											fields?.company_name 
											|| 'company name'
										)}
									</strong>
										<div>
											{store?.customer_company?.name 
												|| '-'
											}
										</div>
								</div>
							</div>

							<div className='col'>
								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											fields?.address_line_1
											|| 'address line 1'
										)}
									</strong>
									<div>
										{store?.customer_company?.primary_site?.address?.address_line_1
											|| '-'
										}
									</div>
								</div>
								
								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											fields?.address_line_2
											|| 'address line 2'
										)}
									</strong>
									<div>
										{store?.customer_company?.primary_site?.address?.address_line_2
											|| '-'
										}
									</div>
								</div>

								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											fields?.address_line_3
											|| 'address line 3'
										)}
									</strong>
									<div>
										{store?.customer_company?.primary_site?.address?.address_line_3
											|| '-'
										}
									</div>
								</div>
							</div>

							<div className='col'>
								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											fields?.town_city
											|| 'town / city'
										)}
									</strong>
									<div>
										{store?.customer_company?.primary_site?.address?.town_city
											|| '-'
										}
									</div>
								</div>

								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											fields?.state_county
											|| 'state / county'
										)}
									</strong>
									<div>
										{store?.customer_company?.primary_site?.address?.state_county
											|| '-'
										}
									</div>
								</div>
							</div>

							<div className='col'>
								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											fields?.postcode_zipcode
											|| 'postcode / zipcode'
										)}
									</strong>
									<div>
										{store?.customer_company?.primary_site?.address?.postcode_zipcode
											|| '-'
										}
									</div>
								</div>

								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											fields?.country_code_iso_3
											|| 'country'
										)}
									</strong>
									<div>
										{store?.customer_company?.primary_site?.address?.country
											|| '-'
										}
									</div>
								</div>
							</div>
						</div>
					</CardBody>
				</Card>

				{/* SYSTEM DETAILS */}
				<Card>
					<CardBody>
						<SubTitle
							className='mb-3 w-100'
							style={{ color: '#464C5E' }}
							title={`${
								crud?.sub_titles?.system
									|| 'System'
								} ${crud?.sub_titles?.details
									|| 'Details'
								}`
							}
						/>

						<div className='row'>
							<div className='col'>
								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
												fields?.system_type
													|| 'system type'
											)
										}
									</strong>
									<div>
										{store?.product_parent_name
											|| '-'
										}
									</div>
								</div>

								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											`${crud?.sub_titles?.system || 'system'} ${fields?.configuration || 'configuration'}`
										)}
									</strong>
									<div>
										{store?.product_child_name
											|| '-'
										}
									</div>
								</div>

								<div className='d-flex flex-column mb-3'>
									<strong>
										{StringHelpers.title(
											fields?.total_system_refrigerant
											|| 'total system refrigerant'
										)}
									</strong>
									<div>
										{store?.total_system_refrigerant
											|| '-'
										}
									</div>
								</div>
							</div>

							{store?.installation_systems?.map((system) =>
								<div className='col' key={system.id}>
									<div className='d-flex flex-column mb-3'>
										<strong>
											{crud?.models?.product_type
												|| 'Product Type'
											}
										</strong>
										<div>
											{system.product_type
												|| '-'
											}
										</div>
									</div>

									<div className='d-flex flex-column mb-3'>
										<strong>
											{crud?.models?.product_line
												|| 'Product Line'
											}
										</strong>
										<div>
											{system.product_line
												|| '-'
											}
										</div>
									</div>

									<div className='d-flex flex-column mb-3'>
										<strong>
											{crud?.models?.product_model
												|| 'Product Model'
											}
										</strong>
										<div>
											{system.product_model
												|| '-'
											}
										</div>
									</div>

									<div className='d-flex flex-column mb-3'>
										<strong>
											{StringHelpers.title(
												fields?.serial_number
												|| 'Product Model'
											)}
										</strong>
										<div>
											{system.serial_number
												|| '-'
											}
										</div>
									</div>

									<div className='d-flex flex-column mb-3'>
										<strong>
											{`${crud?.models?.installation 
													|| 'Installation'} ${
												fields?.location 
													|| 'Location'
												}`
											}
										</strong>
										<div>
											{system.installation_location
												|| '-'
											}
										</div>
									</div>

									{system.product_line === PRODUCT_LINES.CYLINDER
										&& (
											<div className='d-flex flex-column mb-3'>
												<strong>
													{StringHelpers.title(
														fields?.water_circuit_description
														|| 'water circuit description'
													)}
												</strong>
												<div>
													{StringHelpers.limit(
														system.water_circuit_description
														|| '-', 
														MAX_TEXT_LENGTH
													)}
												</div>
											</div>
										)
									}
								</div>
							)}
						</div>
					</CardBody>
				</Card>
				{hasPermissionAccess(Permissions.TOSHIBA_INSTALLATION_AUDIT_LOG_INDEX) 
					&& <InstallationEventLog />
				}
            </WidthConstrainedContainer>

			<ArchiveCancelModal
				modalOpen={activeModal.isOpen}
				modalClose={() =>
					setActiveModal({
						isOpen: false,
						type: null,
					})
				}
				title={StringHelpers.title(
					replaceKeyWithValue(
						activeModal.type === 'archive'
							? crud?.modals?.archive.title 
								|| 'Archive :name?'
							: crud?.modals?.cancel?.title 
								|| 'Cancel Installation?',
						'name',
						crud?.models?.installation || 'Installation'
					)
				)}
				type={activeModal.type || 'cancel'}
				onSuccess={fetchInstallationData}
				installationId={installationId || ''}
				storeData={store}
				dateValue={store?.commissioning_date}
				dateLabel={`${fields?.commissioning || 'Commissioning'} ${fields?.event_at || 'Date'}`}
				engineerLabel={
					`${fields?.commissioning
						|| 'Commissioning'
					} ${crud?.models?.engineer
						|| 'Engineer'
					}`
				}
				systemTypeValue={[store?.product_parent_name, store?.product_child_name].filter(Boolean).join(' - ') || '-'}
			/>

			<ConfirmationModal
				modalOpen={emailModalState.isOpen}
				modalClose={() =>
					setEmailModalState({
						isOpen: false,
						type: '',
					})
				}
				title={
					replaceKeyWithValue(
						crud?.modals?.resend_email?.title
							|| 'Re-send :name email?',
						'name',
						crud?.models?.installation || 'Installation'
					)
				}
				type={emailModalState.type as ConfirmationModalTypes}
				dynamicText={
					replaceKeyWithValue(
						crud?.modals?.resend_email?.description
							|| "You're about to re-send the :name email. If you want to send it to a different email address, enter it below:",
						'name',
						crud?.models?.installation || 'installation'
					)
				}
				defaultEmail={user?.email || ''}
				onSuccess={fetchInstallationData}
				storeData={store}
			/>
        </PageLayout>
    );
};

export default ToshibaInstallation;